import React, { FC } from 'react';

import { graphql } from 'gatsby';
import { Box, Heading, Text, Link } from '@lucid/components-react';
import dayjs from 'dayjs';
import {
  Drupal_NodeNews,
  Drupal_ParagraphFlexiNewsroomPreviewSection
} from 'generated/drupal-graphql';
import { LucidTheme, mqMin, theme } from '@lucid/theme';

interface FlexiNewsroomPreviewSectionProps {
  entity: Drupal_ParagraphFlexiNewsroomPreviewSection;
  newsPreview: Drupal_NodeNews[];
}

const FlexiNewsroomPreviewSection: FC<FlexiNewsroomPreviewSectionProps> = ({
  entity: { fieldFlexiNewsroomHeader, fieldFlexiNewsroomLink },
  newsPreview
}) => {
  return (
    <Box
      as="section"
      css={(theme: LucidTheme) => ({
        label: 'flexNewsroomPreviewSection',
        display: 'flex',
        flexDirection: 'column',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        width: '100%',
        marginTop: theme.space[6],
        marginBottom: theme.space[6],
        paddingLeft: theme.space[6],
        paddingRight: theme.space[6]
      })}
      gridColumn="1 / -1"
    >
      {fieldFlexiNewsroomHeader && (
        <Heading as="h2" variant="headline2XL">
          {fieldFlexiNewsroomHeader}
        </Heading>
      )}
      {fieldFlexiNewsroomLink && (
        <Link hasArrow primary to={fieldFlexiNewsroomLink.url?.path || ''}>
          {fieldFlexiNewsroomLink.title}
        </Link>
      )}
      <Box display="flex" flexWrap="wrap" mt={4} mx={-6} py={4}>
        {newsPreview && !!newsPreview.length ? (
          newsPreview?.map(
            (
              {
                nid,
                title,
                fieldDate,
                fieldImageLink,
                fieldPublisher,
                fieldSourceUrl
              },
              i
            ) =>
              (
                <Box
                  key={nid!}
                  as="article"
                  css={(theme: LucidTheme) => ([
                    {
                      position: 'relative',
                    },
                    i !== newsPreview.length - 1 && {
                      '&::after': {
                        [mqMin.tabletLandscape]: {
                          display: 'initial'
                        },
                        content: '""',
                        display: 'none',
                        position: 'absolute',
                        right: `-${theme.space[6]}px`,
                        height: `calc(100% + ${theme.space[4]}px)`,
                        top: `-${theme.space[4]}px`,
                        background: theme.colors.coolGrey[20],
                        width: '1px'
                      }
                    }
                  ])}
                  m={6}
                  py={[0, 0, 0, 0, 2]}
                  width={[
                    '100%',
                    '100%',
                    '100%',
                    `calc(100% * 1/2 - ${theme.space[6] * 2}px)`,
                    `calc(100% * 1/2 - ${theme.space[6] * 2}px)`,
                    `calc(100% * 1/3 - ${theme.space[6] * 2}px)`
                  ]}
                >
                  <Link
                    rel="noopener noreferrer"
                    target="_blank"
                    to={fieldSourceUrl || '#'}
                  >
                    <Box display="flex">
                      <Box
                        css={{
                          backgroundImage: `url(${fieldImageLink})` || '',
                          backgroundSize: 'cover',
                          backgroundPosition: '50%'
                        }}
                        height="80px"
                        minWidth="80px"
                        mr={4}
                      />
                      <Box>
                        {fieldPublisher && (
                          <Text
                            css={(theme: LucidTheme) => ({
                              color: theme.colors.blue[60],
                              marginTop: 0
                            })}
                            variant="labelXS400"
                          >
                            {fieldPublisher}
                          </Text>
                        )}
                        {title && (
                          <Heading as="h3" mt={0} variant="headlineM">
                            {title}
                          </Heading>
                        )}
                        {fieldDate && (
                          <Text variant="labelXS400">
                            {dayjs(fieldDate!.value!, 'MM-DD-YYYY').format(
                              'MMMM D, YYYY'
                            )}
                          </Text>
                        )}
                      </Box>
                    </Box>
                  </Link>
                </Box>
              )
          )
        ) : null}
      </Box>
    </Box>
  );
};

export default FlexiNewsroomPreviewSection;

export const query = graphql`
  fragment flexiNewsroomPreviewSection on Drupal_ParagraphFlexiNewsroomPreviewSection {
    fieldFlexiNewsroomHeader
    fieldFlexiNewsroomLink {
      title
      url {
        path
      }
    }
  }
`;
